import React, { useEffect, useState } from 'react';

import styled from '@emotion/styled';

import playIcon from '@images/home/icon-play-triangle.svg';

import {
  type BrandImageLayout,
  DESKTOP_BRAND_CARD_HEIGHT,
  DESKTOP_BRAND_CARD_WIDTH,
  MOBILE_BRAND_CARD_HEIGHT,
  getCollapsedBrandImageLayout,
  getExpandedBrandImageStrategy,
  getMobileCollapsedBrandImageLayout,
} from './brand_partner_image_layouts';
import { getHomeBrandPartnerLogo } from './brand_partner_logos';
import {
  HOME_BRAND_PARTNERS_DESKTOP,
  HOME_BRAND_PARTNERS_MOBILE,
  type HomeBrandPartner,
} from './data';
import { EntranceHeadline } from './entrance_headline';
import {
  FeatureTitle,
  HeadingH2,
  HeadingH3,
  Section,
  SectionContainer,
} from './styles';
import { MEDIA, TOKENS } from './tokens';

const MobileStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${TOKENS.space[12]};
  margin-top: ${TOKENS.space[40]};

  ${MEDIA.lg} {
    display: none;
  }
`;

const DesktopRow = styled.div`
  display: none;
  gap: ${TOKENS.space[8]};
  margin-top: ${TOKENS.space[48]};

  ${MEDIA.lg} {
    display: flex;
  }
`;

const PartnerPanel = styled.div<{
  $active: boolean;
  $height: number;
  $isFirst?: boolean;
  $stacked?: boolean;
}>`
  position: relative;
  overflow: hidden;
  border-radius: ${TOKENS.radius.md};
  text-align: left;
  cursor: pointer;
  color: ${TOKENS.color.onDark};
  height: ${({ $height }) => `${$height}px`};
  flex: ${({ $active, $isFirst, $stacked }) => {
    if ($stacked) return '0 0 auto';
    if ($active) return `0 0 ${DESKTOP_BRAND_CARD_WIDTH.expanded}px`;
    if ($isFirst) return `0 0 ${DESKTOP_BRAND_CARD_WIDTH.firstCollapsed}px`;
    return '1 0 0%';
  }};
  transition:
    flex 0.5s ease,
    height 0.5s ease;
  min-width: 0;
  width: 100%;

  ${MEDIA.lg} {
    width: ${({ $active, $isFirst, $stacked }) => {
      if ($stacked) return '100%';
      if ($active) return `${DESKTOP_BRAND_CARD_WIDTH.expanded}px`;
      if ($isFirst) return `${DESKTOP_BRAND_CARD_WIDTH.firstCollapsed}px`;
      return 'auto';
    }};
  }
`;

const PanelImage = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  background: ${TOKENS.color.sectionBgDark};
`;

const PanelImageAsset = styled.img<{
  $layout?: BrandImageLayout;
  $objectPosition: string;
}>`
  position: absolute;
  max-width: none;
  object-fit: cover;
  ${({ $layout, $objectPosition }) =>
    $layout
      ? `
    left: ${$layout.left};
    top: ${$layout.top};
    width: ${$layout.width};
    height: ${$layout.height};
    object-position: ${$layout.objectPosition ?? 'center'};
  `
      : `
    inset: 0;
    width: 100%;
    height: 100%;
    object-position: ${$objectPosition};
  `}
`;

const PartnerLogo = styled.img<{ $height: number; $maxWidth: number }>`
  display: block;
  flex-shrink: 0;
  height: ${({ $height }) => `${$height}px`};
  width: auto;
  max-width: ${({ $maxWidth }) => `${$maxWidth}px`};
`;

const renderPartnerLogo = (partnerId: string) => {
  const logo = getHomeBrandPartnerLogo(partnerId);
  if (!logo) {
    return null;
  }

  return (
    <PartnerLogo
      src={logo.src}
      alt={logo.alt}
      $height={logo.height}
      $maxWidth={logo.maxWidth}
    />
  );
};

const desktopCollapsedOverlay = `linear-gradient(
  0deg,
  ${TOKENS.color.sectionBgDark} 0%,
  rgba(31, 47, 56, 0) 100%
)`;

const desktopExpandedOverlay = `linear-gradient(
  0deg,
  ${TOKENS.color.sectionBgDark} 0%,
  ${TOKENS.color.sectionBgDark} 30%,
  rgba(31, 47, 56, 0) 100%
)`;

const mobileExpandedOverlay = `linear-gradient(
  0deg,
  ${TOKENS.color.sectionBgDark} 0%,
  ${TOKENS.color.sectionBgDark} 30%,
  transparent 100%
)`;

const mobileCollapsedOverlay = `linear-gradient(
  90deg,
  ${TOKENS.color.sectionBgDark} 0%,
  rgba(31, 47, 56, 0.9) 40%,
  rgba(31, 47, 56, 0.7) 62.981%,
  rgba(31, 47, 56, 0.3) 100%
)`;

const brandOverlayBlur = (active: boolean, stacked?: boolean) => {
  if (stacked && !active) return 'none';
  if (active) return 'none';
  return 'blur(1px)';
};

const PanelOverlay = styled.div<{
  $active: boolean;
  $stacked?: boolean;
}>`
  position: absolute;
  inset: 0;
  z-index: 1;
  background: ${({ $active, $stacked }) => {
    if ($stacked && !$active) return 'transparent';
    if ($stacked && $active) return mobileExpandedOverlay;
    if ($stacked) return 'transparent';
    return $active ? desktopExpandedOverlay : desktopCollapsedOverlay;
  }};
  backdrop-filter: ${({ $active, $stacked }) =>
    $stacked && !$active ? 'none' : brandOverlayBlur($active, $stacked)};
  -webkit-backdrop-filter: ${({ $active, $stacked }) =>
    $stacked && !$active ? 'none' : brandOverlayBlur($active, $stacked)};
  transition:
    background 0.5s ease,
    backdrop-filter 0.5s ease,
    -webkit-backdrop-filter 0.5s ease;
`;

const MobileCollapsedScrim = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: ${MOBILE_BRAND_CARD_HEIGHT.collapsed}px;
  z-index: 1;
  background: ${mobileCollapsedOverlay};
  backdrop-filter: blur(1px);
  -webkit-backdrop-filter: blur(1px);
  pointer-events: none;
`;

const PanelContent = styled.div<{ $active: boolean; $stacked?: boolean }>`
  position: relative;
  z-index: 10;
  display: flex;
  height: 100%;
  min-width: 0;
  flex-direction: column;
  justify-content: ${({ $active, $stacked }) =>
    $stacked && !$active ? 'center' : 'flex-end'};
  align-items: stretch;
  padding: ${({ $active, $stacked }) => {
    if ($stacked && $active) {
      return `${TOKENS.space[24]} ${TOKENS.space[16]} ${TOKENS.space[16]}`;
    }
    if ($stacked) {
      return TOKENS.space[16];
    }
    return `${TOKENS.space[24]} ${TOKENS.space[16]} ${TOKENS.space[16]}`;
  }};
`;

const CollapsedBlock = styled.div<{ $visible: boolean }>`
  width: 100%;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  max-height: ${({ $visible }) => ($visible ? 'none' : '0')};
  overflow: hidden;
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
  transition: opacity 0.3s ease;
`;

const CollapsedCopy = styled.div<{ $stacked?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ $stacked }) => ($stacked ? '0' : '2px')};
  padding: ${TOKENS.space[4]};
  min-width: 0;
  width: 100%;
`;

const ExpandedBlock = styled.div<{ $visible: boolean; $stacked?: boolean }>`
  width: 100%;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  max-height: ${({ $visible }) => ($visible ? 'none' : '0')};
  overflow: hidden;
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
  transition: opacity 0.5s ease 500ms;
`;

const Celebrity = styled(HeadingH3)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${TOKENS.color.inverse};
`;

const Show = styled.span`
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: ${TOKENS.font.sans};
  font-size: 14px;
  line-height: 21px;
  font-weight: 500;
  color: ${TOKENS.color.onDarkMuted};
`;

const MobileCelebrity = styled.span`
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: ${TOKENS.font.sans};
  font-size: 15px;
  line-height: 19px;
  font-weight: 500;
  color: ${TOKENS.color.inverse};
`;

const MobileShow = styled.span`
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: ${TOKENS.font.sans};
  font-size: 12px;
  line-height: 20px;
  font-weight: 500;
  color: ${TOKENS.color.onDarkMuted};
`;

const ExpandedTitle = styled(FeatureTitle)`
  display: block;
  white-space: normal;
  color: ${TOKENS.color.inverse};
`;

const DesktopExpandedBody = styled.span`
  display: block;
  margin-top: ${TOKENS.space[8]};
  white-space: normal;
  font-family: ${TOKENS.font.sans};
  font-size: 14px;
  line-height: 21px;
  font-weight: 500;
  color: ${TOKENS.color.onDarkMuted};
`;

const DesktopExpandedCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${TOKENS.space[16]};
  min-width: 0;
  flex: 1;
`;

const DesktopExpandedText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${TOKENS.space[8]};
  min-width: 0;
`;

const MobileExpandedTitle = styled.span`
  display: block;
  white-space: normal;
  font-family: ${TOKENS.font.sans};
  font-size: 15px;
  line-height: 19px;
  font-weight: 500;
  color: ${TOKENS.color.inverse};
`;

const MobileExpandedBody = styled.span`
  display: block;
  margin-top: ${TOKENS.space[8]};
  white-space: normal;
  font-family: ${TOKENS.font.sans};
  font-size: 12px;
  line-height: 20px;
  font-weight: 500;
  color: ${TOKENS.color.onDarkMuted};
`;

const MobileExpandedCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${TOKENS.space[16]};
  min-width: 0;
  flex: 1;
`;

const MobileExpandedText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${TOKENS.space[8]};
`;

const ExpandedRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: ${TOKENS.space[16]};
`;

const MobileExpandedRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: ${TOKENS.space[8]};
  width: 100%;
  padding: ${TOKENS.space[4]};
  box-sizing: border-box;
`;

const PlayLink = styled.a`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  margin-bottom: ${TOKENS.space[4]};
  border-radius: ${TOKENS.radius.pill};
  border: 1px solid ${TOKENS.color.border};
  background: ${TOKENS.color.iconBgLight};
  color: ${TOKENS.color.onDark};
  text-decoration: none;
  transition:
    background 0.15s ease,
    border-color 0.15s ease;

  img {
    width: 24px;
    height: 24px;
  }

  &:hover {
    border-color: ${TOKENS.color.neutral0_20};
    background: ${TOKENS.color.neutral0_10};
  }

  &:active {
    background: ${TOKENS.color.neutral0_12};
  }
`;

const MobilePlayLink = styled(PlayLink)`
  margin-bottom: ${TOKENS.space[4]};
  background: ${TOKENS.color.iconBgLight};
  border-color: ${TOKENS.color.border};

  img {
    width: 24px;
    height: 24px;
  }
`;

const useIsDesktop = () => {
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const update = () => setIsDesktop(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  return isDesktop;
};

const renderPanel = (
  partner: HomeBrandPartner,
  active: boolean,
  height: number,
  stacked: boolean,
  isFirst: boolean,
  onSelect: () => void,
) => {
  const collapsedImageLayout = !active
    ? stacked
      ? getMobileCollapsedBrandImageLayout(partner.id)
      : getCollapsedBrandImageLayout(partner.id)
    : undefined;
  const imageObjectPosition = active
    ? getExpandedBrandImageStrategy(partner.id).objectPosition
    : 'center';

  return (
    <PartnerPanel
      key={partner.id}
      role="button"
      tabIndex={0}
      $active={active}
      $height={height}
      $isFirst={isFirst}
      $stacked={stacked}
      aria-expanded={active}
      aria-label={partner.title}
      onClick={onSelect}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) {
          return;
        }

        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect();
        }
      }}
    >
      <PanelImage aria-hidden="true">
        <PanelImageAsset
          src={partner.image}
          alt=""
          $layout={collapsedImageLayout}
          $objectPosition={imageObjectPosition}
        />
      </PanelImage>
      {stacked && !active && <MobileCollapsedScrim aria-hidden="true" />}
      <PanelOverlay $active={active} $stacked={stacked} />
      <PanelContent $active={active} $stacked={stacked}>
        <CollapsedBlock $visible={!active}>
          <CollapsedCopy $stacked={stacked}>
            {stacked ? (
              <>
                <MobileCelebrity>{partner.celebrity}</MobileCelebrity>
                <MobileShow>{partner.show}</MobileShow>
              </>
            ) : (
              <>
                <Celebrity>{partner.celebrity}</Celebrity>
                <Show>{partner.show}</Show>
              </>
            )}
          </CollapsedCopy>
        </CollapsedBlock>
        <ExpandedBlock $visible={active} $stacked={stacked}>
          {stacked ? (
            <MobileExpandedRow>
              <MobileExpandedCopy>
                {active && renderPartnerLogo(partner.id)}
                <MobileExpandedText>
                  <MobileExpandedTitle>
                    {partner.featured
                      ? (partner.expandedTitle ?? partner.title)
                      : partner.title}
                  </MobileExpandedTitle>
                  <MobileExpandedBody>{partner.description}</MobileExpandedBody>
                </MobileExpandedText>
              </MobileExpandedCopy>
              {partner.videoId && (
                <MobilePlayLink
                  href={`https://www.youtube.com/watch?v=${partner.videoId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Watch ${partner.title} trailer on YouTube`}
                  onClick={(event) => event.stopPropagation()}
                >
                  <img src={playIcon} alt="" />
                </MobilePlayLink>
              )}
            </MobileExpandedRow>
          ) : (
            <ExpandedRow>
              <DesktopExpandedCopy>
                {active && renderPartnerLogo(partner.id)}
                <DesktopExpandedText>
                  <ExpandedTitle>
                    {partner.featured
                      ? (partner.expandedTitle ?? partner.title)
                      : partner.title}
                  </ExpandedTitle>
                  <DesktopExpandedBody>
                    {partner.description}
                  </DesktopExpandedBody>
                </DesktopExpandedText>
              </DesktopExpandedCopy>
              {partner.videoId && (
                <PlayLink
                  href={`https://www.youtube.com/watch?v=${partner.videoId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Watch ${partner.title} trailer on YouTube`}
                  onClick={(event) => event.stopPropagation()}
                >
                  <img src={playIcon} alt="" />
                </PlayLink>
              )}
            </ExpandedRow>
          )}
        </ExpandedBlock>
      </PanelContent>
    </PartnerPanel>
  );
};

export const HomeBrandsSection: React.FC = () => {
  const isDesktop = useIsDesktop();
  const partners = isDesktop
    ? HOME_BRAND_PARTNERS_DESKTOP
    : HOME_BRAND_PARTNERS_MOBILE;
  const netflixIndex = partners.findIndex(
    (partner) => partner.id === 'netflix',
  );
  const initialIndex = netflixIndex >= 0 ? netflixIndex : 0;
  const [activeIndex, setActiveIndex] = useState(initialIndex);

  useEffect(() => {
    setActiveIndex(initialIndex);
  }, [initialIndex, isDesktop]);

  return (
    <Section $variant="brands">
      <SectionContainer>
        <EntranceHeadline as={HeadingH2} style={{ maxWidth: 570 }}>
          Trusted by people and brands who can&apos;t afford to get it wrong
        </EntranceHeadline>

        <MobileStack>
          {partners.map((partner, index) =>
            renderPanel(
              partner,
              index === activeIndex,
              index === activeIndex
                ? MOBILE_BRAND_CARD_HEIGHT.expanded
                : MOBILE_BRAND_CARD_HEIGHT.collapsed,
              true,
              index === 0,
              () => setActiveIndex(index),
            ),
          )}
        </MobileStack>

        <DesktopRow>
          {partners.map((partner, index) =>
            renderPanel(
              partner,
              index === activeIndex,
              DESKTOP_BRAND_CARD_HEIGHT,
              false,
              index === 0,
              () => setActiveIndex(index),
            ),
          )}
        </DesktopRow>
      </SectionContainer>
    </Section>
  );
};
